// Port of ios/Sources/ViewModels/MessageTimestamp.swift.
//
// The clock time on a message, and — for a reader who is not on Mexico's clock — what that same
// instant was AT HOME.
//
// "When the user is on another time zone, for the timestamp of the messages show in parenthesis
// the timestamp of Mexico and the current timestamp on a side" (user, 2026-09-16). The workspace
// runs on `America/Mexico_City`: shifts, deadlines and "send it before six" are all said in that
// clock, and a colleague reading from another country has to do the arithmetic in their head on
// every single message — the kind of sum people get wrong once and then stop trusting.
//
// Two rules, and the second is what keeps this quiet:
//
// 1. The reader's OWN time stays the headline. It is the clock on their wall and the one their
//    next meeting is in; the home time is the annotation beside it.
// 2. It appears only when the two clocks actually DISAGREE — decided by comparing the two offsets
//    **at the message's own instant**, never by comparing identifiers. A machine set to
//    `America/Monterrey` reads the same hour as Mexico City, and "09:41 (09:41 MX)" on every row
//    would be noise on a row with no space for any. Comparing at the instant also gets the days
//    around a DST change right.
//
// A decision-for-decision twin of Android's `MessageTimestamp`, tests included.
import { clock as clockText } from './clockStyle';
import { civil, localTimeZone } from './time';

/** The workspace's clock. Everything the company says about time is said in this one. */
const homeZoneIdentifier = 'America/Mexico_City';

/** The time on a message: the reader's clock, and the workspace's when they differ. */
export interface MessageTimestampParts {
  /** The message's time on the READER's clock — always shown. */
  local: string;
  /** The same instant on the workspace's clock, or null when it is the same clock. */
  home: string | null;
}

export interface MessageTimestampOptions {
  /** The reader's zone; undefined = the machine's. */
  deviceZone?: string;
  /** Defaults to `homeZoneIdentifier`. */
  homeZone?: string;
}

/** Seconds east of UTC that `timeZone` reads at the instant `ms` (Foundation's `secondsFromGMT(for:)`). */
function secondsFromGMT(ms: number, timeZone: string | undefined): number {
  const c = civil(ms, timeZone);
  const wall = Date.UTC(c.year, c.month - 1, c.day, c.hour, c.minute, c.second);
  return Math.round((wall - Math.floor(ms / 1000) * 1000) / 1000);
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

/**
 * "HH:mm" on `timeZone`'s clock. Built from the civil reading rather than an Intl formatter: the
 * Swift formatter is a fixed `HH:mm` pattern (24-hour, Latin digits in both UI languages), and
 * Intl's `hour: '2-digit'` can still yield "24:05" or a locale's own hour cycle.
 */
function time(ms: number, timeZone: string | undefined): string {
  const c = civil(ms, timeZone);
  return clockText(c.hour, c.minute);
}

function parts(epochMillis: number, options: MessageTimestampOptions = {}): MessageTimestampParts {
  const deviceZone = options.deviceZone ?? localTimeZone();
  const homeZone = options.homeZone ?? homeZoneIdentifier;
  const local = time(epochMillis, deviceZone);
  // At this instant, not in general — see the header comment.
  if (secondsFromGMT(epochMillis, deviceZone) === secondsFromGMT(epochMillis, homeZone)) {
    return { local, home: null };
  }
  return { local, home: time(epochMillis, homeZone) };
}

/** The whole label, already assembled: what a view puts on a row. */
function label(
  epochMillis: number,
  annotated: (local: string, home: string) => string,
  options: MessageTimestampOptions = {},
): string {
  const p = parts(epochMillis, options);
  if (p.home == null) return p.local;
  return annotated(p.local, p.home);
}

export const MessageTimestamp = {
  homeZoneIdentifier,
  homeZone: homeZoneIdentifier,
  parts,
  label,
  secondsFromGMT,
} as const;
